import React from 'react'
import styled, { css } from 'styled-components'
import {
  flexbox,
  FlexboxProps,
  space,
  SpaceProps,
  layout,
  LayoutProps,
  variant,
} from 'styled-system'
import { buildComponent } from '../utils/buildComponent'

export const statusMap: Dictionary<{
  variant: ChipVariants
  text: string
  btn: string
}> = {
  ACTIVE: {
    variant: 'green' as ChipVariants,
    text: 'active',
    btn: 'Manage Incentives',
  },
  UPDATING: {
    variant: 'blue',
    text: 'updating',
    btn: 'Manage Incentives',
  },
  PENDING: {
    variant: 'purple',
    text: 'pending',
    btn: 'Manage Incentives',
  },
  CANCELLING: {
    variant: 'red',
    text: 'cancelling',
    btn: 'Manage Incentives',
  },
  CANCELLED: {
    variant: 'grey',
    text: 'cancelled',
    btn: 'New Incentives',
  },
  DISABLED: {
    variant: 'disabled',
    text: 'off',
    btn: 'New Incentives',
  },
  PAUSED: {
    variant: 'disabled',
    text: 'paused',
    btn: 'Manage Incentives',
  },
}

const Chip = styled.div<FlexboxProps & SpaceProps & LayoutProps>`
  ${(props) => props.theme.typography.caption};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  width: fit-content;
  max-width: 100%;
  text-transform: capitalize;
  white-space: nowrap;
  user-select: none;

  ${(props) =>
    variant({
      variants: {
        green: {
          backgroundColor: props.theme.palette.success.green300,
          color: props.theme.palette.success.green800,
        },
        red: {
          backgroundColor: props.theme.palette.error.red300,
          color: props.theme.palette.error.red800,
        },
        purple: {
          backgroundColor: props.theme.palette.secondary.purple300,
          color: props.theme.palette.secondary.purple600,
        },
        grey: {
          backgroundColor: props.theme.palette.grey.grey300,
          color: props.theme.palette.grey.grey800,
        },
        disabled: {
          backgroundColor: props.theme.palette.grey.grey100,
          color: props.theme.palette.text.disabled,
        },
        yellow: {
          backgroundColor: props.theme.palette.warning.yellow300,
          color: props.theme.palette.warning.yellow800,
        },
        blue: {
          backgroundColor: props.theme.palette.accent.blue300,
          color: props.theme.palette.accent.blue800,
        },
        pink: {
          backgroundColor: props.theme.palette.primary.pink200,
          color: props.theme.palette.primary.pink600,
        },
        regular: {
          backgroundColor: props.theme.palette.primary.pink200,
          color: props.theme.palette.text.secondary,
        },
        transparent: {
          backgroundColor: props.theme.palette.transparent,
          color: props.theme.palette.text.placeholder,
        },
      },
    })}

  ${flexbox}
  ${space}
  ${layout}
`

const FilterChip = styled.div<
  FlexboxProps &
    SpaceProps &
    LayoutProps & { isSelected?: boolean; isInteractive: boolean }
>`
  ${(props) => props.theme.typography.action4};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  border: ${(props) => `1px solid ${props.theme.palette.grey.grey300}`};
  background-color: ${(props) => props.theme.palette.white};
  text-transform: capitalize;
  color: ${(props) => props.theme.palette.text.secondary};
  min-width: ${(props) => props.theme.pxToRem(65)};
  width: fit-content;
  user-select: none;

  ${(props) =>
    props.isInteractive &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${(props) => props.theme.palette.primary.pink300};
        border-color: ${(props) => props.theme.palette.text.secondary};
      }
    `}

  ${(props) =>
    props.isSelected
      ? {
          backgroundColor: props.theme.palette.primary.pink300,
          borderColor: props.theme.palette.primary.pink300,
          color: props.theme.palette.white,
          '&:hover': {
            backgroundColor: props.theme.palette.primary.pink700,
            borderColor: props.theme.palette.primary.pink700,
            color: props.theme.palette.white,
          },
        }
      : {}}

  ${flexbox}
  ${space}
  ${layout}
`

export type ChipVariants =
  | 'regular'
  | 'purple'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'red'
  | 'pink'
  | 'grey'
  | 'disabled'
  | 'transparent'
  | 'filter'

export interface ChipProps
  extends FlexboxProps,
    SpaceProps,
    LayoutProps,
    React.HtmlHTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  variant?: ChipVariants
  selected?: boolean
  isInteractive?: boolean
}

export default buildComponent((props: ChipProps) => {
  const { variant = 'green', selected, isInteractive = true, children } = props

  if (variant === 'filter') {
    return (
      <FilterChip
        isSelected={selected}
        isInteractive={isInteractive}
        {...props}
      >
        {children}
      </FilterChip>
    )
  }

  return (
    <Chip variant={variant} {...props}>
      {children}
    </Chip>
  )
}, 'Chip')
